import {
  ChangeEvent,
  LegacyRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { SvgClearCross } from '../../assets/images/index'
import { debounce, isEmpty, noop, isEqual, get } from 'lodash'
import {
  Control,
  Controller,
  FieldErrorsImpl,
  FieldValues,
  GlobalError,
  Path,
} from 'react-hook-form'
import { DropdownType, NAMESPACE } from '../../utils'
import { SvgClientArrow } from '../../icons'
import ReactDOM from 'react-dom'
import { ShortcutKeys } from '../../utils/tab-indexing-contants'
interface OutsideClickHandlerProps {
  refs: React.RefObject<HTMLElement>[]
  handler: () => void
}

const useOutsideClick = ({ refs, handler }: OutsideClickHandlerProps) => {
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        !refs?.some((ref: React.RefObject<HTMLElement>) =>
          ref?.current?.contains(event.target as Node)
        )
      ) {
        handler()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [refs, handler])
}

interface OptionContainerPropsType<T> {
  idOption?: number
  option: DropdownType<T> | null
  handleValueSelect: (e: DropdownType<T>) => void
  isSelected: boolean
  isHighlighted: boolean
}

const OptionContainer = <T,>({
  idOption,
  option,
  handleValueSelect,
  isSelected,
  isHighlighted,
}: OptionContainerPropsType<T>) => {
  const handleSelected = () => {
    if (!isEmpty(option)) {
      handleValueSelect(option)
    }
  }
  return (
    <li
      data-qa-id={idOption?.toString()}
      className={`p-3 border-t border-border-100 hover:bg-secondary-25 flex pointer-events-auto cursor-pointer w-full text-black font-primary ${
        isSelected ? 'bg-secondary-300' : ''
      }  ${isHighlighted ? 'bg-secondary-100' : ''}`}
      title={option?.tooltip?.toString() ?? option?.label}
      onMouseDown={handleSelected}
      onKeyDown={(e) => {
        if (e.key === ShortcutKeys.Enter || e.key === ShortcutKeys.Space) {
          e.preventDefault()
          handleSelected()
        }
      }}
      role="option"
      aria-selected={isSelected}
      tabIndex={-1}
    >
      <span className="max-w-[calc(100%-0.938rem)] truncate font-light text-xs">
        {option?.label}
      </span>
    </li>
  )
}

interface OptionBoxPropsType<T> {
  options: DropdownType<T>[]
  excludedParentRef: React.RefObject<HTMLElement>
  handleValueSelect: (e: DropdownType<T>) => void
  handleOptionFalse: () => void
  toggleOption: () => void
  isOpenOptions: boolean
  selectedOption: DropdownType<T> | null
  fetchItems?: (page: number, limit: number) => Promise<void>
  page?: number
  setPage?: React.Dispatch<React.SetStateAction<number>>
  limit?: number
  virtualScroll?: boolean
  optionZIndex: number
  position: {
    top: number
    left: number
    width: number
  }
  scrollPositionRef: React.MutableRefObject<number>
  loading: boolean
  hasMore: boolean
  highlightedIndex: number
  openOptionsBorder: string
}

interface SearchInput<T> {
  selectedOption: DropdownType<T> | null
  placeholder: string
  disabled: boolean
  handleOptionSearch: (e: ChangeEvent<HTMLInputElement>) => void
  inpref: LegacyRef<HTMLInputElement>
  hasPermission: boolean
  options: DropdownType<T>[]
  tabIndex?: number
}

const SearchInput = <T,>({
  selectedOption,
  placeholder,
  disabled,
  handleOptionSearch,
  inpref,
  hasPermission,
  options,
  tabIndex,
}: SearchInput<T>) => {
  const [selectedValue, setSelectedValue] = useState(
    selectedOption?.label ?? ''
  )
  const [tooltip, setTooltip] = useState(
    selectedOption?.tooltip ?? selectedOption?.label ?? ''
  )

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    const fieldValue = e?.currentTarget?.value
    setSelectedValue(fieldValue)
    setTooltip(fieldValue)
    handleOptionSearch(e)
  }
  const handleBlur = (e: ChangeEvent<HTMLInputElement>) => {
    const fieldValue = e?.currentTarget?.value
    const isPresent = options.find((option) => option.label === fieldValue)
    if (isPresent) {
      setSelectedValue(fieldValue)
      setTooltip(isPresent?.tooltip ?? isPresent?.label ?? '')
    } else {
      setSelectedValue('')
      if (e?.currentTarget) e.currentTarget.value = ''
      setTooltip('')
    }
  }
  useEffect(() => {
    setSelectedValue(selectedOption?.label ?? '')
    setTooltip(selectedOption?.tooltip ?? selectedOption?.label ?? '')
  }, [selectedOption])
  return (
    <input
      ref={inpref}
      type="text"
      placeholder={placeholder}
      title={tooltip?.toString()}
      className={` outline-none border-none box-content bg-transparent m-0 block min-w-0 w-full font-light text-xs pt-3.5 pb-3 ${
        disabled ? 'text-accent-500' : 'text-current'
      } h-5 `}
      disabled={disabled}
      onChange={handleOnChange}
      onBlur={handleBlur}
      value={hasPermission ? selectedValue : NAMESPACE.REDACTED}
      tabIndex={disabled ? -1 : tabIndex ?? 0}
    />
  )
}

const OptionBox = <T,>({
  options,
  excludedParentRef,
  handleValueSelect,
  handleOptionFalse,
  toggleOption,
  isOpenOptions,
  selectedOption,
  virtualScroll,
  fetchItems,
  setPage,
  limit,
  optionZIndex,
  position,
  scrollPositionRef,
  loading,
  hasMore,
  highlightedIndex,
  openOptionsBorder,
}: OptionBoxPropsType<T>) => {
  const dropdownOptionRef = useRef<HTMLDivElement>(null)
  useOutsideClick({
    refs: [excludedParentRef, dropdownOptionRef],
    handler: handleOptionFalse,
  })

  const handleScroll = () => {
    const dropdownMenu = dropdownOptionRef.current
    if (!dropdownMenu) return

    const scrollTop = dropdownMenu.scrollTop
    const scrollHeight = dropdownMenu.scrollHeight
    const offsetHeight = dropdownMenu.offsetHeight

    if (scrollTop + offsetHeight >= scrollHeight - 10 && hasMore && !loading) {
      scrollPositionRef.current = scrollTop
      setPage &&
        setPage((prevPage) => {
          const newPage = prevPage + 1
          fetchItems && fetchItems(newPage, limit ?? 0)
          return newPage
        })
    }
  }

  useEffect(() => {
    if (dropdownOptionRef.current && virtualScroll) {
      dropdownOptionRef.current.addEventListener('scroll', handleScroll)
    }

    return () => {
      if (dropdownOptionRef.current && virtualScroll) {
        dropdownOptionRef.current.removeEventListener('scroll', handleScroll)
      }
    }
  }, [loading, hasMore])

  useEffect(() => {
    if (dropdownOptionRef.current) {
      dropdownOptionRef.current.scrollTop = scrollPositionRef.current
    }
  }, [options])

  useEffect(() => {
    if (isOpenOptions && highlightedIndex >= 0 && dropdownOptionRef.current) {
      const optionElements = dropdownOptionRef.current.querySelectorAll('li')
      const highlightedElement = optionElements[highlightedIndex]
      if (highlightedElement) {
        const containerRect = dropdownOptionRef.current.getBoundingClientRect()
        const elementRect = highlightedElement.getBoundingClientRect()
        const isAbove = elementRect.top < containerRect.top
        const isBelow = elementRect.bottom > containerRect.bottom
        if (isAbove || isBelow) {
          highlightedElement.scrollIntoView({
            block: 'nearest',
            behavior: 'smooth',
          })
        }
      }
    }
  }, [highlightedIndex, isOpenOptions])

  return (
    <div
      ref={dropdownOptionRef}
      onClick={toggleOption}
      className={` font-extraLight overflow-y-auto  mt-[0.006rem] mr-5  rounded-b   border-r border-l border-b  bg-accent-0 max-h-[12.375rem]   group-hover:border-t-0
           ${isOpenOptions ? 'border-secondary-500' : ' border-accent-600'}
          group-hover:border-secondary-500 ${openOptionsBorder}`}
      style={{
        position: 'absolute',
        top: position.top,
        left: position.left,
        width: position.width + 'px',
        zIndex: optionZIndex,
      }}
    >
      <div className="">
        <ul className="">
          {isEmpty(options) ? (
            <li
              className={`p-3 border-t border-border-100 flex pointer-events-auto cursor-not-allowed w-full text-accent-500`}
            >
              <span className="max-w-[calc(100%-0.938rem)] truncate font-light text-xs">
                No Options
              </span>
            </li>
          ) : (
            options?.map((option: DropdownType<T>, index: number) => (
              <OptionContainer
                idOption={index + 1}
                key={`${option?.value}_${index}`}
                option={option}
                handleValueSelect={handleValueSelect}
                isSelected={selectedOption?.value === option?.value}
                isHighlighted={highlightedIndex === index}
              />
            ))
          )}
          {loading && (
            <div className="text-center p-2 text-accent-500 font-light text-xs">
              Loading...
            </div>
          )}
        </ul>
      </div>
    </div>
  )
}

interface FieldErrors<U extends FieldValues> {
  root?: Record<string, GlobalError> & GlobalError
  [key: string]: FieldErrorsImpl<U> | GlobalError | undefined
}

interface SelectDropDownPropTypes<T, U extends FieldValues> {
  dataQaId?: string
  label: string
  options: DropdownType<T>[]
  name: string
  defaultValue?: string | null
  disabled?: boolean
  required?: boolean
  onChange: (value: DropdownType<T>, name: string) => void
  placeholder?: string
  className?: string
  errors?: FieldErrors<U>
  isBordered?: boolean
  icon?: (
    disabled: boolean,
    closeDropdownOptions: () => void,
    tabIndex?: number
  ) => React.ReactElement
  isCrossable?: boolean
  fetchOptions?: (
    page: number,
    limit: number,
    value?: string
  ) => Promise<Response>
  page?: number
  setPage?: React.Dispatch<React.SetStateAction<number>>
  limit?: number
  virtualScroll?: boolean
  optionZIndex?: number
  hasPermission?: boolean
  searchLength?: number
  tabIndex?: number
  height?: string
  customPadding?: string
  openOptionsBorder?: string
}

export const SelectDropDown = <T, U extends FieldValues>({
  dataQaId,
  label = '',
  name = '',
  options,
  defaultValue,
  icon,
  disabled = false,
  required = false,
  onChange = noop,
  placeholder = 'Select Option',
  className = '',
  errors,
  isBordered = true,
  isCrossable = false,
  fetchOptions,
  limit,
  searchLength = 3,
  virtualScroll,
  optionZIndex = 50,
  hasPermission = true,
  tabIndex,
  height = 'h-[1.25rem]',
  customPadding = '',
  openOptionsBorder = '',
}: SelectDropDownPropTypes<T, U>) => {
  const [isOpenOptions, setIsOpenOption] = useState<boolean>(false)
  const textFieldRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState({ top: 0, left: 0, width: 200 })
  const [selectedOption, setSelectedOption] = useState<DropdownType<T> | null>(
    null
  )
  const [currentPage, setCurrentPage] = useState(0)
  const [searchResultOption, setSearchResultOption] = useState<
    DropdownType<T>[]
  >(options || [])
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const scrollPositionRef = useRef<number>(0)
  const selectedValueShown = useRef(false)
  const [renderKey, setRenderKey] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1) // For keyboard navigation
  const inpref = useRef<HTMLInputElement>(null)

  const dropdownRef = useRef<HTMLDivElement>(null)
  const clearButtonRef = useRef<HTMLDivElement>(null)
  const toggleOption = () => {
    setTimeout(() => {
      setIsOpenOption((prevOption) => !prevOption)
    }, 0)
    setHighlightedIndex(-1) // Reset highlight when opening/closing
  }

  const blurInput = () => {
    inpref?.current?.blur()
  }

  const handleOptionFalse = () => {
    setIsOpenOption(false)
  }
  const clearAll = () => {
    setSelectedOption(null)
    onChange({ label: '', value: '' }, name)
    // Keep focus on dropdown after clearing
    setTimeout(() => {
      inpref?.current?.focus()
    }, 0)
  }
  const handleValueSelect = (selectedValue: DropdownType<T>) => {
    setSelectedOption(structuredClone(selectedValue))
    onChange(selectedValue, name)
    blurInput()
    toggleOption()
    setHighlightedIndex(-1)
    // Return focus to dropdown after selection
    setTimeout(() => {
      inpref?.current?.focus()
    }, 0)
  }

  const debouncedHandleInputChange = useCallback(
    debounce(async (fieldValue) => {
      const lowerFieldValue = fieldValue?.toLowerCase()

      if (
        lowerFieldValue.length >= searchLength ||
        lowerFieldValue.length === 0
      ) {
        if (fetchOptions && limit) {
          setLoading(true)
          const data = await fetchOptions(0, limit, inpref.current?.value)
          if (Array.isArray(data) && data.length < limit) {
            setHasMore(false)
          } else if (Array.isArray(data) && data.length === limit) {
            setHasMore(true)
          }
          setCurrentPage(0)
          setLoading(false)
        }
      }
      setLoading(false)
    }, 300),
    [options]
  )

  const handleOptionSearch = (e: ChangeEvent<HTMLInputElement>) => {
    debouncedHandleInputChange(e?.currentTarget?.value)
    setIsOpenOption(true)
    setHighlightedIndex(-1) // Reset highlight on search
  }

  const updatePosition = () => {
    if (isOpenOptions && textFieldRef.current) {
      const textFieldRect = textFieldRef.current.getBoundingClientRect()
      if (textFieldRect.bottom + window.scrollY < 110) {
        handleOptionFalse()
      }
      setPosition({
        top: textFieldRect.bottom + window.scrollY,
        left: textFieldRect.left + window.scrollX,
        width: textFieldRect.width,
      })
    }
  }

  const fetchItems = async (page: number, limit: number) => {
    if (loading || !hasMore || !fetchOptions) return

    setLoading(true)
    try {
      const response = (await fetchOptions(
        page,
        limit,
        inpref.current?.value && inpref.current?.value === selectedOption?.label
          ? ''
          : inpref.current?.value
      )) as unknown as DropdownType<T>[]
      // const data: DropdownType<T>[] = await response.json() //IMP: Commenting and updating as response?.json was returning an error

      if (limit && response?.length < limit) {
        setHasMore(false)
      } else setHasMore(true)
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    // Handle clicks outside the dropdown
    const handleDocumentClick = (e: MouseEvent) => {
      if (!isOpenOptions) return
      const clickedDropdown = dropdownRef.current?.contains(e.target as Node)
      const clickedOptions = document
        .getElementById('dropdown-root')
        ?.contains(e.target as Node)
      if (!clickedDropdown && !clickedOptions) {
        setIsOpenOption(false)
        setHighlightedIndex(-1)
      }
    }

    // Handle focus changes (for tabbing)

    const handleFocusChange = (e: FocusEvent) => {
      if (!isOpenOptions) return
      // Check if focus moved outside both dropdown and its options
      const focusInDropdown = dropdownRef.current?.contains(e.target as Node)
      const focusInOptions = document
        .getElementById('dropdown-root')
        ?.contains(e.target as Node)

      const focusInClearButton = clearButtonRef.current?.contains(
        e.target as Node
      )
      if (!focusInDropdown && !focusInOptions && !focusInClearButton) {
        setIsOpenOption(false)
        setHighlightedIndex(-1)
      }
    }

    document.addEventListener('mousedown', handleDocumentClick)
    document.addEventListener('focusin', handleFocusChange)

    return () => {
      document.removeEventListener('mousedown', handleDocumentClick)
      document.removeEventListener('focusin', handleFocusChange)
    }
  }, [isOpenOptions])

  useEffect(() => {
    const handleScrollOrResize = (e: Event) => {
      const target = e.target as HTMLElement
      if (isOpenOptions && target?.parentElement?.id !== 'dropdown-root') {
        updatePosition()
      }
    }
    window.addEventListener('scroll', handleScrollOrResize, true)
    window.addEventListener('resize', handleScrollOrResize)
    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true)
      window.removeEventListener('resize', handleScrollOrResize)
    }
  }, [isOpenOptions])

  useEffect(() => {
    if (isOpenOptions) updatePosition()
  }, [isOpenOptions])

  useEffect(() => {
    if (!isEmpty(options)) {
      const selected = options?.filter((option: DropdownType<T>) =>
        isEqual(option?.value, defaultValue)
      )
      if (selected?.length && !selectedValueShown.current) {
        selectedValueShown.current = true
        setSelectedOption(selected?.[0] || null)
      }
    }
    setSearchResultOption(options || [])
    setRenderKey((prev) => !prev)
  }, [defaultValue, options])

  const checkIfSelectedOptionEmpty = (
    selectedOption: DropdownType<T, string> | null
  ) => {
    return selectedOption?.value !== null && selectedOption?.value !== undefined
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) {
      return
    }

    // Open dropdown on Enter or ArrowDown
    if (
      (e.key === ShortcutKeys.Enter || e.key === ShortcutKeys.ArrowDown) &&
      !isOpenOptions
    ) {
      e.preventDefault()
      setIsOpenOption(true)
      if (!isEmpty(searchResultOption) && e.key === ShortcutKeys.ArrowDown) {
        setHighlightedIndex(0)
      }
      return
    }

    // Close dropdown on Escape
    if (e.key === ShortcutKeys.Escape && isOpenOptions) {
      e.preventDefault()
      setIsOpenOption(false)
      setHighlightedIndex(-1)
      return
    }

    // Navigation within dropdown
    if (isOpenOptions && !isEmpty(searchResultOption)) {
      if (e.key === ShortcutKeys.ArrowDown) {
        e.preventDefault()
        setHighlightedIndex((prevIndex) =>
          Math.min(prevIndex + 1, searchResultOption.length - 1)
        )
      } else if (e.key === ShortcutKeys.ArrowUp) {
        e.preventDefault()
        setHighlightedIndex((prevIndex) =>
          prevIndex === -1 ? 0 : Math.max(prevIndex - 1, 0)
        )
      } else if (e.key === ShortcutKeys.Enter && highlightedIndex !== -1) {
        e.preventDefault()
        handleValueSelect(searchResultOption[highlightedIndex])
      } else if (e.key === ShortcutKeys.Tab) {
        // Close dropdown on Tab
        setIsOpenOption(false)
        setHighlightedIndex(-1)
        // Let default tab behavior continue
      }
    }
  }

  const portalContainer =
    document.getElementById('dropdown-root') || document.createElement('div')

  if (!document.getElementById('dropdown-root')) {
    portalContainer.id = 'dropdown-root'
    document.body.appendChild(portalContainer)
  }

  return (
    <div
      ref={dropdownRef}
      data-qa-id={dataQaId ?? name ?? ''}
      className={` group  relative w-full font-primary   ${
        disabled
          ? ' cursor-not-allowed bg-accent-75'
          : 'cursor-pointer select-none'
      }  focus:outline-none
        focus:border-transparent`}
      onClick={isEmpty(options) ? noop : disabled ? noop : toggleOption}
      onKeyDown={handleKeyDown}
      tabIndex={disabled ? -1 : tabIndex ?? 0}
      aria-haspopup="listbox"
      aria-expanded={isOpenOptions}
      aria-labelledby={`${name}-label`}
    >
      <div
        className={`  w-full box-border relative  items-center rounded ${className} focus-within:outline-none`}
        ref={textFieldRef}
      >
        {label ? (
          <div
            className={`${
              selectedOption?.value || placeholder || isOpenOptions
                ? 'translate-x-[0.875rem] t translate-y-[-0.75rem]'
                : 'translate-x-[0.875rem] t translate-y-[0.5rem]'
            } transition-all duration-100 ease-in-out flex text-base p-0 origin-top-left whitespace-nowrap overflow-hidden text-ellipsis max-w-[calc(100%-1.75rem)] absolute left-0 top-0  z-30 bg-white  pointer-events-none ${
              disabled ? 'text-accent-300' : 'text-accent-900'
            }`}
          >
            <label
              className={` font-extraLight  text-xs leading-1 block whitespace-nowrap font-extraligh overflow-hidden text-ellipsis max-w-[calc(100% - 1.5rem]  
                 ${isOpenOptions ? 'text-secondary-500' : ''}
                ${
                  disabled
                    ? 'text-accent-300'
                    : 'group-hover:text-secondary-500 group-focus-within:text-secondary-500'
                }`}
              title={label}
              htmlFor={name}
            >
              {label}
            </label>
            {required ? (
              <span
                className={`ml-1 text-xs leading-1   ${
                  disabled ? 'text-accent-300' : 'text-error-500'
                }`}
              >
                *
              </span>
            ) : null}
          </div>
        ) : null}
        <div
          className={`z-[2] pl-4 pr-2.5 pt-2 pb-2 relative ${customPadding}`}
        >
          <div
            data-qa-id={`add-field-dropdown`}
            className={`flex items-center justify-between w-full ${height}`}
          >
            <SearchInput
              selectedOption={selectedOption}
              placeholder={placeholder}
              disabled={disabled}
              handleOptionSearch={handleOptionSearch}
              inpref={inpref}
              hasPermission={hasPermission}
              options={searchResultOption}
              tabIndex={tabIndex}
            />

            <div className="flex gap-2 items-center">
              {isCrossable &&
              checkIfSelectedOptionEmpty(selectedOption) &&
              !disabled ? (
                <div
                  className="py-1 pl-1"
                  ref={clearButtonRef}
                  onClick={disabled ? noop : clearAll}
                  tabIndex={disabled ? -1 : tabIndex ?? 0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      e.stopPropagation()
                      if (!disabled) clearAll()
                    }
                  }}
                  role="button"
                  aria-label="Clear selection"
                >
                  <SvgClearCross />
                </div>
              ) : null}
              {icon ? (
                icon(disabled, handleOptionFalse, tabIndex)
              ) : (
                <SvgClientArrow
                  className={`transform transition-transform duration-200  ${
                    isOpenOptions ? 'rotate-180' : ''
                  } ${disabled ? 'invisible' : ''}`}
                />
              )}
            </div>
          </div>
        </div>

        <fieldset
          aria-hidden="true"
          className={`text-left ${isBordered ? 'border' : ''} absolute ${
            label
              ? selectedOption?.value || placeholder || isOpenOptions
                ? 'top-[-0.938rem]'
                : 'top-0'
              : 'top-0'
          } bottom-0 left-0 right-0 px-2 pointer-events-none rounded border-solid overflow-hidden min-w-0 font-extraLight outline-none
                    border-border-150
                 
                    ${
                      isOpenOptions
                        ? 'border-b-0 rounded-b-none group-hover:border-b-0 group-hover:rounded-b-none border-secondary-500'
                        : ''
                    }
                    ${
                      disabled
                        ? 'border-border-200 bg-accent-50'
                        : 'group-hover:border-secondary-500 group-focus-within:border-secondary-500 group-focus-within:border'
                    }
                    ${
                      !isEmpty(errors?.[name])
                        ? 'border-error-500'
                        : 'border-accent-150'
                    } ${openOptionsBorder}`}
        >
          {label ? (
            <legend
              className={`${
                selectedOption?.value || placeholder || isOpenOptions
                  ? 'invisible'
                  : 'hidden'
              } z-[2]  bg-white float-none max-w-full select-none  p-1 box-border `}
            >
              <span
                className={` dropdown-label overflow-hidden whitespace-nowrap  text-ellipsis font-extraLight
                        ${isOpenOptions ? 'text-secondary-500' : ''}
                        ${
                          disabled
                            ? 'text-accent-300'
                            : 'group-hover:text-secondary-500 group-focus-within:text-secondary-500 '
                        } ${openOptionsBorder}`}
              >
                {label}
              </span>
              {required ? (
                <span
                  className={`ml-1 text-xs leading-1  ${
                    disabled ? 'text-accent-300' : 'text-error-500'
                  }`}
                >
                  *
                </span>
              ) : null}
            </legend>
          ) : null}
        </fieldset>
      </div>

      {isOpenOptions
        ? ReactDOM.createPortal(
            <OptionBox
              excludedParentRef={dropdownRef}
              handleValueSelect={handleValueSelect}
              handleOptionFalse={handleOptionFalse}
              toggleOption={toggleOption}
              options={searchResultOption}
              isOpenOptions={isOpenOptions}
              selectedOption={selectedOption}
              fetchItems={fetchItems}
              limit={limit}
              page={currentPage}
              setPage={setCurrentPage}
              virtualScroll={virtualScroll}
              optionZIndex={optionZIndex}
              scrollPositionRef={scrollPositionRef}
              position={position}
              loading={loading}
              hasMore={hasMore}
              key={renderKey.toString()}
              highlightedIndex={highlightedIndex}
              openOptionsBorder={openOptionsBorder}
            />,
            portalContainer
          )
        : null}
      {errors &&
        name &&
        (get(errors, `${name}.type`) as string) === 'required' && (
          <span className="text-error-500 text-xs font-primary font-extraLight">
            {NAMESPACE?.REQUIRED_FIELD}
          </span>
        )}
      {errors &&
        name &&
        (get(errors, `${name}.type`) as string) !== 'required' && (
          <span className="text-error-500 text-xs">
            {errors?.[name]?.['message']?.toString()}
          </span>
        )}
    </div>
  )
}

interface ControlledDropDownPropTypes<T, U extends FieldValues> {
  dataQaId?: string
  control: Control<U>
  name: Path<U>
  errors?: FieldErrors<U>
  onChange: (value: DropdownType<T>, name: string) => void
  label?: string
  options: DropdownType<T>[]
  required?: boolean
  className?: string
  disabled?: boolean
  placeholder?: string
  isBordered?: boolean
  isCrossable?: boolean
  defaultValue?: string
  fetchOptions?: (page: number, limit: number) => Promise<Response>
  page?: number
  setPage?: React.Dispatch<React.SetStateAction<number>>
  limit?: number
  virtualScroll?: boolean
  hasPermission?: boolean
  searchLength?: number
  tabIndex?: number
  height?: string
  customPadding?: string
  icon?: (disabled: boolean, closeOptions: () => void) => React.ReactElement
  openOptionsBorder?: string
}

const PaginatedControlledDropDown = <T, U extends FieldValues>({
  dataQaId,
  control,
  name,
  errors,
  onChange,
  label = '',
  options = [],
  className = '',
  disabled = false,
  required,
  placeholder = 'Select Option',
  isBordered = true,
  isCrossable = false,
  defaultValue,
  fetchOptions,
  limit,
  page,
  setPage,
  virtualScroll,
  searchLength,
  tabIndex,
  height,
  customPadding = '',
  openOptionsBorder = '',
  ...rest
}: ControlledDropDownPropTypes<T, U>) => {
  return (
    <Controller
      name={name}
      control={control}
      rules={{ required: required }}
      disabled={disabled}
      render={({ field }) => (
        <div className="relative w-[100%]">
          <input
            className="absolute w-[2px] left-4 top-2 opacity-0 border-none"
            ref={field.ref}
            tabIndex={-1}
          />
          <SelectDropDown
            {...rest}
            name={field?.name}
            disabled={disabled}
            label={label}
            errors={errors}
            defaultValue={defaultValue ? defaultValue : field?.value}
            options={options}
            virtualScroll={virtualScroll}
            fetchOptions={fetchOptions}
            limit={limit}
            page={page}
            setPage={setPage}
            required={required}
            className={className}
            isBordered={isBordered}
            searchLength={searchLength}
            onChange={(selectedOption) => {
              field.onChange(selectedOption.value)
              onChange(selectedOption, name)
            }}
            placeholder={placeholder}
            isCrossable={isCrossable}
            dataQaId={dataQaId}
            tabIndex={tabIndex}
            height={height}
            customPadding={customPadding}
            openOptionsBorder={openOptionsBorder}
          />
        </div>
      )}
    />
  )
}

export default PaginatedControlledDropDown
